import { t } from 'ttag'
import { useMutation, useQueryClient } from 'react-query'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import ReactImageFallback from 'react-image-fallback'
import { Redirect, useParams } from 'react-router-dom'

import userHub from '@h2/assets/svg/icons/userHub.svg'
import { Text } from '@h2/components/typography/text'
import { api, errors } from '@h2/api'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { useForm } from '@h2/common/components/hooks/useForm'
import { Button } from '@h2/components/buttons/button'
import { Heading } from '@h2/components/typography/heading'
import { Flex } from '@h2/components/layout/flex'
import { PB97T, PB90T } from '@h2/components/colors'
import educatorSvg from '@h2/assets/svg/illustrations/navigatorAccount.svg'
import { Alert } from '@h2/components/display/alert'
import { Link } from '@h2/common/components/link'
import { Skeleton } from '@h2/components/loading/skeleton'
import { useIsUserOver13 } from '@h2/common/components/user/checkUser'

const UserImage = styled(ReactImageFallback)`
  border: 1px solid ${PB90T};
  background-color: ${PB97T};
  width: 50px;
  max-height: 50px;
  border-radius: 50%;
`
const EducatorImage = styled.img`
  max-width: 30%;
`
const NavigatorConnection = () => {
  const { navigatorCode } = useParams()
  const [requestedUser, setRequestedUser] = useState(null)
  const [apiError, setApiError] = useState(null)
  const queryClient = useQueryClient()
  const [showRequestConfirmation, setRequestConfirmation] = useState(false)
  const isOver13 = useIsUserOver13()
  const { register, error, value, isInvalid, handleOnSubmit, dispatch } = useForm({
    user(form) {
      if (!form.user?.trim()) {
        return t`A valid navigator code is required`
      }
    }
  })
  const { mutate, isLoading: isSubmitting } = useMutation(
    body =>
      api.fetch('/management/finduser', {
        method: 'POST',
        version: 'v1',
        body: JSON.stringify(body)
      }),
    {
      onSuccess: ({ user, error: submissionError }) => {
        if (submissionError) {
          dispatch({
            type: 'validate',
            payload: { user: [submissionError] }
          })
        } else {
          setRequestedUser(user)
        }
      },
      onError: err => {
        const formErrors = errors.getValidation(err)

        if (errors.getStatus(err) === 429) {
          setApiError(t`Too many failed attempts. Please try again in 60 minutes.`)
        }

        if (formErrors) {
          dispatch({ type: 'validate', payload: formErrors })
        }
      }
    }
  )

  useEffect(() => {
    if (navigatorCode) {
      mutate({ user: navigatorCode })
    }
  }, [mutate, navigatorCode])
  const { mutate: requestManagement, isLoading: isMakingRequest } = useMutation(
    () => {
      const code = navigatorCode || value.user

      api.fetch(`/management/request/association/${code}`, {
        method: 'POST',
        version: 'v1',
        body: JSON.stringify({})
      })
    },
    {
      onSuccess: () => {
        setRequestedUser(null)
        setRequestConfirmation(true)
        queryClient.refetchQueries(['userManagers'], { refetchType: 'all' })
      },
      onError: err => {
        const formErrors = errors.getValidation(err)

        if (formErrors) {
          dispatch({ type: 'validate', payload: formErrors })
        }

        const err2 = errors.getError(err)

        if (err2) {
          setApiError(t`An error occurred while requesting navigator.`)
        }
      }
    }
  )

  if (showRequestConfirmation) {
    if (navigatorCode) {
      return <Redirect to="/" />
    }

    return (
      <div>
        <Heading level="3">{t`Your management request has been completed`}</Heading>
      </div>
    )
  }

  if (requestedUser) {
    return (
      <div>
        {apiError && <Alert isDismissible={false} message={apiError} type="error" />}
        <Text size="large" weight="bold">
          {isOver13 ? t`You are requesting that access to your account be granted to:` : `Users under 13 years old cannot be connected to navigator.`}
        </Text>
        <Flex alignItems="center" direction="row" gap="10px">
          <UserImage
            alt={t`User Thumbnail`}
            fallbackImage={userHub}
            src={requestedUser?.image}
            className="img-circle"
          />
          <Text size="large" margin="0">
            {requestedUser?.name}
          </Text>
        </Flex>
        {isOver13 && (
          <div style={{ marginTop: '50px' }}>
            <Button
              size="large"
              display="default"
              isLoading={isMakingRequest}
              onClick={requestManagement}>
              {t`Proceed`}
            </Button>
            &#160;
            <Button size="large" display="red" onClick={() => setRequestedUser(null)}>
              {t`Cancel`}
            </Button>
          </div>
        )}
      </div>
    )
  }

  /*
   * If we received a navigator code as a page param, show a skeleton instead
   * of the entry page
   */
  if (navigatorCode) {
    return <Skeleton />
  }

  return (
    <Flex gap="15px">
      <EducatorImage src={educatorSvg} />
      <div>
        <Text>{t`Share your learning progress with a navigator using their 6-letter navigator code.`}</Text>
        {apiError && <Alert isDismissible={false} message={apiError} type="error" />}
        <form onSubmit={handleOnSubmit(form => mutate(form))}>
          <Field
            isRequired
            label={t`Navigator Code`}
            desc={error.user}
            status={error.user ? 'error' : 'default'}>
            <TextInput {...register('user')} />
          </Field>
          <div style={{ padding: '15px 0' }}>
            <Button
              size="large"
              type="submit"
              isLoading={isSubmitting}
              isDisabled={isSubmitting || isInvalid}>
              {t`Request Navigator`}
            </Button>
          </div>
        </form>
        <Flex justifyContent="right">
          <Link href="/personalprofile/edit/management">{t`Review Existing Navigators`}</Link>
        </Flex>
      </div>
    </Flex>
  )
}

export { NavigatorConnection }
